import { Component, Input, ChangeDetectionStrategy } from '@angular/core'

import { TrygroupFactorAnalysis } from '../../../../services/trygroup-dashboard/trygroup-personal-analysis.service'
import {
  getFactorRankLabel as resolveFactorRankLabel,
  getKaIdentifier as resolveKaIdentifier,
} from '../ka-labels'
import {
  buildDiagnosisViewpointCode,
  deriveKeyActionDiagnosisStatus,
  getPatternTypeLabel,
} from './key-action-diagnosis'

@Component({
  selector: 'app-trygroup-key-action-diagnosis-table',
  standalone: true,
  template: `
    <section class="diagnosis-section" aria-labelledby="diagnosis-title">
      <div class="diagnosis-header">
        <div>
          <h2 id="diagnosis-title" class="diagnosis-title">
            キーアクション分解診断
          </h2>
          <p class="diagnosis-subtitle">
            優先強化因子を観点ごとに分解し、直近の発生状況を診断します
          </p>
        </div>
        <div class="status-legend" aria-label="改善優先度の凡例">
          <span class="legend-item status-priority">× 優先課題</span>
          <span class="legend-item status-warning">▲ 注意ポイント</span>
          <span class="legend-item status-ok">○ 安定</span>
          <span class="legend-item status-good">◎ 良い調子</span>
        </div>
      </div>

      <div class="factor-tables">
        @for (factor of factorAnalyses; track factor.rank) {
          <article class="factor-table-panel">
            <div class="factor-table-header">
              <span
                class="factor-rank-badge"
                [class.factor-rank-primary]="factor.rank === 1"
                [class.factor-rank-secondary]="factor.rank === 2"
              >
                {{ getFactorRankLabel(factor.rank) }}
              </span>
              <h3 class="factor-name">
                {{ getKaIdentifier(factor.kaId) }}
                @if (factor.kaDefinition != null) {
                  {{ factor.kaDefinition.kaName }}
                }
              </h3>
            </div>

            <div
              class="table-scroll"
              tabindex="0"
              role="region"
              [attr.aria-label]="getFactorTableAriaLabel(factor)"
            >
              <table>
                <caption class="visually-hidden">
                  {{
                    getFactorRankLabel(factor.rank)
                  }}
                  のキーアクション分解診断
                </caption>
                <thead>
                  <tr>
                    <th scope="col">診断観点</th>
                    <th scope="col">改善優先度</th>
                    <th scope="col">課題タイプ</th>
                  </tr>
                </thead>
                <tbody>
                  @for (
                    pattern of factor.patternSummaries;
                    track pattern.patternId
                  ) {
                    @let diagnosisStatus =
                      deriveDiagnosisStatus(pattern.hitCount);
                    @let patternTypeLabel =
                      resolvePatternTypeLabel(
                        pattern.patternType,
                        pattern.hitCount
                      );
                    <tr>
                      <td class="viewpoint-cell">
                        <div class="viewpoint-content">
                          <span class="viewpoint-code">
                            {{
                              buildViewpointCode(factor.kaId, pattern.patternId)
                            }}
                          </span>
                          <span class="viewpoint-name">
                            {{ pattern.patternName }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div class="improvement-priority-content">
                          <span
                            class="status-badge"
                            [class.status-priority]="
                              diagnosisStatus.key === 'priority'
                            "
                            [class.status-warning]="
                              diagnosisStatus.key === 'warning'
                            "
                            [class.status-ok]="diagnosisStatus.key === 'ok'"
                            [class.status-good]="diagnosisStatus.key === 'good'"
                          >
                            {{ diagnosisStatus.symbol }}
                            {{ diagnosisStatus.label }}
                          </span>
                          <span class="occurrence-text">
                            （直近{{ pattern.totalCount }}回中{{
                              pattern.hitCount
                            }}回発生）
                          </span>
                        </div>
                      </td>
                      <td>
                        @if (patternTypeLabel != null) {
                          <span
                            class="type-chip"
                            [class.type-ng-talk]="
                              pattern.patternType === 'ng_talk'
                            "
                            [class.type-missing]="
                              pattern.patternType === 'missing'
                            "
                          >
                            {{ patternTypeLabel }}
                          </span>
                        } @else {
                          <span class="type-empty">—</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </article>
        }
      </div>

      <div class="type-guide">
        <span class="type-guide-title">補足：課題タイプの見方</span>
        <div class="type-guide-items">
          <p class="type-guide-item">
            <strong>NGトーク：</strong>
            言ってしまっている発言がNG。伝え方や言い回しが逆効果になっている状態です。
          </p>
          <p class="type-guide-item">
            <strong>必要な説明不足：</strong>
            本来言うべきことが言えていない。必要な説明や一言が抜けている状態です。
          </p>
        </div>
      </div>
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .diagnosis-section {
        padding: 24px;
        margin-bottom: 24px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .diagnosis-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
      }

      .diagnosis-title,
      .factor-name {
        margin: 0;
        color: #0f172a;
      }

      .diagnosis-title {
        font-size: 22px;
        font-weight: 800;
        line-height: 1.3;
      }

      .diagnosis-subtitle {
        margin: 4px 0 0;
        color: #64748b;
        font-size: 13px;
        line-height: 1.6;
      }

      .status-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        justify-content: flex-end;
      }

      .legend-item,
      .status-badge,
      .type-chip {
        display: inline-flex;
        align-items: center;
        min-height: 26px;
        padding: 2px 9px;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        border-radius: 4px;
      }

      .status-priority {
        color: #be123c;
        background: #fff1f2;
        border: 1px solid #fecdd3;
      }

      .status-good {
        color: #047857;
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
      }

      .status-warning,
      .status-ok {
        color: #475569;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
      }

      .type-guide {
        display: grid;
        gap: 8px;
        padding: 10px 14px;
        margin-top: 16px;
        color: #475569;
        font-size: 12px;
        line-height: 1.6;
        background: #f8fafc;
        border-radius: 6px;
      }

      .type-guide-title {
        color: #334155;
        font-weight: 800;
      }

      .type-guide-items {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px 24px;
      }

      .type-guide-item {
        margin: 0;
      }

      .factor-tables {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
        margin-top: 20px;
      }

      .factor-table-panel {
        min-width: 0;
        overflow: hidden;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .factor-table-panel:only-child {
        grid-column: 1 / -1;
      }

      .factor-table-header {
        display: flex;
        gap: 10px;
        align-items: center;
        padding: 14px 16px;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
      }

      .factor-rank-badge {
        display: inline-flex;
        align-items: center;
        min-height: 26px;
        padding: 3px 10px;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        border-radius: 5px;
      }

      .factor-rank-primary {
        color: #1d4ed8;
        background: #dbeafe;
      }

      .factor-rank-secondary {
        color: #ea580c;
        background: #fff7ed;
      }

      .factor-name {
        font-size: 16px;
        font-weight: 800;
        line-height: 1.4;
      }

      .table-scroll {
        overflow-x: auto;
      }

      .table-scroll:focus-visible {
        outline: 3px solid #2563eb;
        outline-offset: -3px;
      }

      table {
        width: 100%;
        /* 3列のうち改善優先度・課題タイプは padding-inline 10px 前提。
           540px 時は順に 167.4px（内容幅 147.4px）・129.6px（内容幅 109.6px）となり、
           「▲ 注意ポイント」バッジと「必要な説明不足」chip が各セル内に収まる。 */
        min-width: 540px;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 13px;
      }

      /* 3列構成は診断観点 / 改善優先度 / 課題タイプ = 45 / 31 / 24%。
         発生テキストは改善優先度列内で折り返し、長い課題タイプ chip の幅を確保する。 */
      th:nth-child(1) {
        width: 45%;
      }

      th:nth-child(2) {
        width: 31%;
      }

      th:nth-child(3) {
        width: 24%;
      }

      th,
      td {
        padding: 12px 16px;
        text-align: left;
        vertical-align: middle;
        border-bottom: 1px solid #e2e8f0;
      }

      /* 改善優先度・課題タイプ列は padding を詰め、table-fixed でも chip が隣列に被らない幅を確保する */
      th:nth-child(n + 2),
      td:nth-child(n + 2) {
        padding-inline: 10px;
      }

      th {
        color: #475569;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        background: #ffffff;
      }

      tbody tr:last-child td {
        border-bottom: 0;
      }

      .viewpoint-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        align-items: flex-start;
      }

      .viewpoint-code {
        color: #334155;
        font-weight: 800;
      }

      .viewpoint-name {
        color: #0f172a;
        font-weight: 700;
        line-height: 1.5;
      }

      .improvement-priority-content {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        align-items: center;
      }

      .occurrence-text {
        color: #64748b;
        font-size: 12px;
        line-height: 1.5;
      }

      .type-ng-talk {
        color: #be123c;
        background: #fff1f2;
        border: 1px solid #fecdd3;
      }

      .type-missing {
        color: #1d4ed8;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
      }

      .type-empty {
        color: #94a3b8;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      /* viewport 1280px 時（ページ幅 1232px）のパネル幅は約 584px。
         パネル左右の border を除くテーブル表示幅は約 582px で、min-width 540px を
         42px 上回るため2カラム表示でも横スクロールは発生しない。1280px 未満は1カラムに落とす。 */
      @media (width < 1280px) {
        .factor-tables {
          grid-template-columns: minmax(0, 1fr);
        }
      }

      @media (width < 768px) {
        .type-guide-items {
          grid-template-columns: minmax(0, 1fr);
        }
      }

      @media (width < 700px) {
        .diagnosis-section {
          padding: 16px;
        }

        .diagnosis-header {
          flex-direction: column;
        }

        .status-legend {
          justify-content: flex-start;
        }
      }
    `,
  ],
})
export class TrygroupKeyActionDiagnosisTableComponent {
  @Input({ required: true }) factorAnalyses!: TrygroupFactorAnalysis[]

  readonly buildViewpointCode = buildDiagnosisViewpointCode
  readonly deriveDiagnosisStatus = deriveKeyActionDiagnosisStatus
  readonly getFactorRankLabel = resolveFactorRankLabel
  readonly getKaIdentifier = resolveKaIdentifier
  readonly resolvePatternTypeLabel = getPatternTypeLabel

  /** 因子名を含む診断テーブルのアクセシブルな名称を返す。 */
  getFactorTableAriaLabel(factor: TrygroupFactorAnalysis): string {
    const rankLabel = this.getFactorRankLabel(factor.rank)
    const kaIdentifier = this.getKaIdentifier(factor.kaId)
    if (factor.kaDefinition == null) {
      return `${rankLabel} ${kaIdentifier} キーアクション分解診断表`
    }

    return `${rankLabel} ${kaIdentifier}${factor.kaDefinition.kaName} キーアクション分解診断表`
  }
}
